/**
 * Browser-side privacy consent state — the one place every tag, first-party
 * event and client integration asks "may I?".
 *
 * Storage: localStorage['truwe_privacy_consent_v1'].
 * Shape:   { analytics, advertising, updatedAt, noticeVersion, source }
 *
 * Rules:
 *  - Optional categories default to `unknown` and are treated as NOT allowed
 *    in every build. Nothing is preselected.
 *  - The legacy single marketing flag (`truwe_marketing_consent`) is migrated
 *    on first read so nobody who already chose is asked again, and it is kept
 *    in step on every write so the existing Meta / Consent Mode v2 / ledger
 *    plumbing keeps working untouched.
 *  - Changes broadcast in this tab (custom event) and other tabs (storage).
 *  - No extra personal data is collected to record a choice.
 */
import {
  OPTIONAL_CATEGORIES,
  PRIVACY_NOTICE_VERSION,
  type OptionalCategory,
} from "./categories";
import { getConsent, setConsent } from "@/lib/meta/consent";

export type CategoryStatus = "granted" | "denied" | "unknown";

export interface PrivacyConsentState {
  analytics: CategoryStatus;
  advertising: CategoryStatus;
  /** ISO timestamp of the last explicit choice, or null when never asked. */
  updatedAt: string | null;
  noticeVersion: string;
  source: string;
}

export const PRIVACY_CONSENT_KEY = "truwe_privacy_consent_v1";
export const PRIVACY_CONSENT_EVENT = "truwe:privacy-consent-change";

export const UNDECIDED_STATE: PrivacyConsentState = {
  analytics: "unknown",
  advertising: "unknown",
  updatedAt: null,
  noticeVersion: PRIVACY_NOTICE_VERSION,
  source: "default",
};

function normalise(value: unknown): CategoryStatus {
  return value === "granted" || value === "denied" ? value : "unknown";
}

/** Pure parser — exported for tests. */
export function parseConsentState(
  raw: string | null,
  legacy: CategoryStatus = "unknown",
): PrivacyConsentState {
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<PrivacyConsentState>;
      return {
        analytics: parsed.source === "migrated_cookie_choice" ? "unknown" : normalise(parsed.analytics),
        advertising: normalise(parsed.advertising),
        updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : null,
        noticeVersion:
          typeof parsed.noticeVersion === "string" ? parsed.noticeVersion : PRIVACY_NOTICE_VERSION,
        source: typeof parsed.source === "string" ? parsed.source : "stored",
      };
    } catch {
      // Corrupt category storage is not permission to fall back to an old grant.
      return { ...UNDECIDED_STATE };
    }
  }
  // A legacy advertising choice cannot establish analytics consent.
  if (legacy === "granted" || legacy === "denied") {
    return {
      analytics: "unknown",
      advertising: legacy,
      updatedAt: null,
      noticeVersion: PRIVACY_NOTICE_VERSION,
      source: "migrated_cookie_choice",
    };
  }
  return { ...UNDECIDED_STATE };
}

// Page-local fallback only when browser storage is unavailable. This never
// reconstructs a deleted visitor identity or overrides readable storage.
let volatileState: PrivacyConsentState | null = null;
let volatileWindow: Window | null = null;

export function getConsentState(): PrivacyConsentState {
  if (typeof window === "undefined") return { ...UNDECIDED_STATE };
  if (volatileWindow === window && volatileState) return { ...volatileState };
  try {
    return parseConsentState(window.localStorage.getItem(PRIVACY_CONSENT_KEY), getConsent());
  } catch {
    return volatileWindow === window && volatileState ? { ...volatileState } : { ...UNDECIDED_STATE };
  }
}

/** True once the visitor has answered both optional categories. */
export function hasDecided(state: PrivacyConsentState = getConsentState()): boolean {
  return state.analytics !== "unknown" && state.advertising !== "unknown";
}

export function isAllowed(
  category: OptionalCategory,
  state: PrivacyConsentState = getConsentState(),
): boolean {
  return state[category] === "granted";
}

export type CategorySelection = Partial<Record<OptionalCategory, boolean>>;

/**
 * Write the visitor's choice. `source` records where they chose
 * (banner_accept_all, banner_reject_optional, settings, footer…).
 */
export function setConsentState(
  selection: CategorySelection,
  source: string,
): PrivacyConsentState {
  const current = getConsentState();
  const next: PrivacyConsentState = {
    ...current,
    updatedAt: new Date().toISOString(),
    noticeVersion: PRIVACY_NOTICE_VERSION,
    source,
  };
  for (const category of OPTIONAL_CATEGORIES) {
    const chosen = selection[category];
    if (chosen === undefined) continue;
    next[category] = chosen ? "granted" : "denied";
  }

  if (typeof window !== "undefined") {
    volatileWindow = window;
    volatileState = { ...next };
    try {
      window.localStorage.setItem(PRIVACY_CONSENT_KEY, JSON.stringify(next));
      volatileState = null;
    } catch {
      /* private mode — the choice still applies for this page view */
    }
    // Keep the existing advertising plumbing (Consent Mode v2 update, Meta
    // eligibility, server ledger write) in step with the category choice.
    if (next.advertising !== current.advertising && next.advertising !== "unknown") {
      setConsent(next.advertising, source);
    }
    try {
      window.dispatchEvent(new CustomEvent(PRIVACY_CONSENT_EVENT, { detail: next }));
    } catch {
      /* ignore */
    }
  }
  return next;
}

export function acceptAll(source = "banner_accept_all"): PrivacyConsentState {
  return setConsentState({ analytics: true, advertising: true }, source);
}

export function rejectOptional(source = "banner_reject_optional"): PrivacyConsentState {
  return setConsentState({ analytics: false, advertising: false }, source);
}

/** Subscribe to changes in this tab and in any other open tab. */
export function onConsentStateChange(
  cb: (state: PrivacyConsentState) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const local = (e: Event) => {
    const detail = (e as CustomEvent<PrivacyConsentState>).detail;
    cb(detail ?? getConsentState());
  };
  const cross = (e: StorageEvent) => {
    if (e.key === null || e.key === PRIVACY_CONSENT_KEY || e.key === "truwe_marketing_consent") {
      volatileState = null;
      cb(getConsentState());
    }
  };
  window.addEventListener(PRIVACY_CONSENT_EVENT, local);
  window.addEventListener("storage", cross);
  return () => {
    window.removeEventListener(PRIVACY_CONSENT_EVENT, local);
    window.removeEventListener("storage", cross);
  };
}
